/** @jsxRuntime classic */
/** @jsx jsx */
import * as React from 'react';
import {useEffect, useReducer, useRef, useState} from 'react';
import {css, jsx} from '@emotion/react';
import tokens from '@flexe/ui-tokens';
import {Inbound, InboundState} from '../shared/InboundInterfaces';
import {ApiResponse, ApiResponseV2, Company, Document, Reservation} from '../../shared/CommonInterfaces';
import ShipperInboundContext from '../contexts/ShipperInboundContext';
import InboundDetailsHeaderPanel from '../shared/InboundDetailsHeaderPanel';
import InboundDocuments, {InboundDocumentsRef} from '../shared/InboundDocuments';
import {InboundSummary} from '../shared/InboundSummary';
import WarehouseInformation from './WarehouseInformation';

interface Props {
  inboundId: number;
  authenticityToken: string;
}
const InboundDeliveryDetails: React.FunctionComponent<Props> = (props) => {
  const {inboundService, documentsService, containersService, packingListService, warehouseService} = React.useContext(
    ShipperInboundContext
  );

  const [errors, setErrors] = React.useState<string[]>([]);
  const [successMessage, setSuccessMessage] = React.useState<string>(null);
  const [inbound, setInbound] = React.useState<Inbound>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [shipperCompany, setShipperCompany] = React.useState<Company>(null);
  const [reservation, setReservation] = React.useState<Reservation>(null);
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [documentIdsToDelete, setDocumentIdsToDelete] = useState<number[]>([]);
  const [editMode, toggleEditMode] = useReducer((isEditing) => !isEditing, false);
  const [containerLoaded, setContainerLoaded] = React.useState<boolean>(false);
  const documentsRef = useRef<InboundDocumentsRef>(null);

  useEffect(() => {
    loadInboundDetails();
  }, []);

  const handleApiError = (err: string[], response: ApiResponse<any> | ApiResponseV2 | null) => {
    if (response?.errors?.length > 0) {
      err[0] += ':';
      response.errors.map((error: {detail: string}) => err.push(error.detail));
    }
    setErrors(err);
  };

  const loadInboundDetails = async () => {
    const response = await inboundService.getInboundById(props.inboundId);
    if (response?.errors?.length > 0 || !response?.data) {
      setContainerLoaded(false);
      handleApiError([`Failed to load inbound detail #${props.inboundId}`], response);
    } else {
      setErrors([]);
      const inboundResponse = response.data as Inbound;

      const inboundReservation: Reservation = {
        id: inboundResponse.reservation.id
      };
      const inboundShipperCompany: Company = {
        id: inboundResponse.reservation.shipperId,
        name: inboundResponse.reservation.shipperName
      };
      setInbound(inboundResponse);
      setReservation(inboundReservation);
      setShipperCompany(inboundShipperCompany);
      setContainerLoaded(true);
    }
    setIsLoading(false);
  };

  const onUpdateInstructions = (newInstructions: string) => {
    setInbound((currentInbound) => ({
      ...currentInbound,
      instructions: newInstructions
    }));
  };

  const triggerDocumentDelete = async () => {
    // The child component now handles the API call and its own state update.
    await documentsRef.current?.handleDeleteDocument();
  };

  const triggerCancelEdit = () => {
    // Tell the child component to revert any staged deletions.
    documentsRef.current?.handleCancelButtonClicked();
    // The parent still controls its own edit mode.
    if (editMode) {
      toggleEditMode();
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  return (
    <div className="content">
      {inbound && reservation && (
        <div className="new-inbound-header-pane">
          <div id="inbound-details-page">
            <InboundDetailsHeaderPanel
              inbound={inbound}
              containersService={containersService}
              onUpdateInstructions={onUpdateInstructions}
              errors={[]}
              reservation={reservation}
              enableInboundPackaging={false}
              onInputTextMap={null}
              inputTextMap={new Map<string, string>()}
              isEditing={false}
              isShipper={true}
              isEditMode={editMode}
              handleRefreshPage={handleRefreshPage}
              shipperEditProps={{
                onCancelEditClicked: triggerCancelEdit,
                onDeleteDocs: triggerDocumentDelete,
                setInbound,
                toggleEditMode
              }}
            />

            <div id="inbound-additional-details" className="warehouse-info-and-docs">
              <WarehouseInformation container={inbound} reservation={reservation} />
              <InboundDocuments
                ref={documentsRef}
                isShipper={true}
                inbound={inbound}
                reservation={reservation}
                onDocumentSaved={null}
                handleIsBolUploaded={null}
                isContainerDeliveryArrived={inbound.actualArrivalTime !== null}
                isEditMode={editMode}
                handleRefreshPage={() => window.location.reload()}
                documentsService={documentsService}
                authenticationToken={props.authenticityToken}
              />
            </div>
          </div>
        </div>
      )}

      {shipperCompany && (
        <div
          css={css`
            padding: ${tokens.spacing.v300.value}px;
          `}
        >
          <InboundSummary
            containersService={containersService}
            inboundService={inboundService}
            packingListService={packingListService}
            warehouseService={warehouseService}
            reservation={reservation}
            lpnReceiveOnly={true}
            inbound={inbound}
            inboundState={inbound.state as InboundState}
            shipperProps={{
              isShipper: true,
              shipperCompanyId: shipperCompany.id,
              handleRefreshPage,
              handleApiError
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InboundDeliveryDetails;
